'use client'

import { Spacer, Card, InputText, Button, Text, Validation } from '@vinted/web-ui'

import { FormProvider, useForm } from 'react-hook-form'

import { useMemo } from 'react'

import { camelCase } from 'lodash'

import { useSession } from '@marketplace-web/shared/session'
import { ContentLoader, useFormValidationMessage } from '@marketplace-web/shared/ui-helpers'

import { useTranslate } from '@marketplace-web/shared/i18n'
import { useLocation } from '@marketplace-web/shared/browser'

import { GeneralError } from '../../../components/Register/GeneralError'

import useSubmitVatNumber from './useSubmitVatNumber'

const TRANSLATION_PREFIX = 'business.add_vat_page'

enum VatField {
  VatNumber = 'vatNumber',
}

type FormData = {
  vatNumber: string
}

const initialVatFormData: FormData = {
  vatNumber: '',
}

const isValueInObject = <T extends string | number>(
  value: unknown,
  object: Record<string, T>,
): value is T => Object.values(object).includes(value as T)

type AddVatFormProps = {
  handleSucccess: () => void
}

const AddVatFormContent = ({ handleSucccess }: AddVatFormProps) => {
  const { user } = useSession()
  const { apiErrors, onSubmitVatNumber, showGeneralError, loading } = useSubmitVatNumber()
  const { goToPreviousPage: back } = useLocation()
  const translate = useTranslate()

  const formMethods = useForm<FormData>({
    defaultValues: initialVatFormData,
    mode: 'onChange',
  })

  const {
    register,
    getValues,
    setError,
    formState: { errors },
  } = formMethods

  const validationMessage = useFormValidationMessage(errors, TRANSLATION_PREFIX)

  useMemo(() => {
    if (!apiErrors) return

    apiErrors.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (!isValueInObject(fieldName, VatField)) return

      setError(fieldName, {
        type: 'manual',
        message: value,
      })
    })
  }, [apiErrors, setError])

  const handleFormSubmit = async () => {
    const vatDataSubmit = {
      userId: user?.id || 0,
      countryCode: user?.country_code || '',
      vatNumber: getValues('vatNumber'),
    }

    if (!vatDataSubmit.vatNumber) {
      setError(VatField.VatNumber, {
        type: 'manual',
        message: translate('business.add_vat_page.validation.empty'),
      })

      return
    }

    await onSubmitVatNumber({ submitVatData: vatDataSubmit, onSuccess: handleSucccess })
  }

  function renderBusinessNameValidation() {
    const errorMessage = validationMessage(VatField.VatNumber)

    if (errorMessage) {
      return <Validation text={errorMessage} theme="warning" />
    }

    return null
  }

  if (loading) {
    return <ContentLoader size={ContentLoader.Size.XLarge} />
  }

  if (showGeneralError) {
    return <GeneralError />
  }

  return (
    <>
      <Text as="h1" type={Text.Type.Heading} text={translate('business.add_vat_page.title')} />
      <Spacer size={Spacer.Size.XLarge} />
      <Text as="p" type={Text.Type.Body} text={translate('business.add_vat_page.content')} />
      <Spacer size={Spacer.Size.XLarge} />
      <FormProvider {...formMethods}>
        <Card>
          <div className="u-flexbox u-flex-direction-row u-align-items-center u-ui-padding-horizontal-medium u-ui-padding-vertical-medium u-fill-width">
            <div className="u-flexbox u-fill-width">
              <Text
                as="span"
                type={Text.Type.Title}
                text={translate('business.add_vat_page.field_label')}
                bold
              />
            </div>
            <div className="u-flexbox u-fill-width">
              <InputText
                {...register(VatField.VatNumber, {
                  required: true,
                })}
                placeholder={translate('business.add_vat_page.field_placeholder')}
                validation={renderBusinessNameValidation()}
              />
            </div>
          </div>
        </Card>
        <Spacer size={Spacer.Size.X2Large} />

        <Button
          onClick={handleFormSubmit}
          type={Button.Type.Submit}
          theme="primary"
          text={translate('business.add_vat_page.submit_button')}
          disabled={loading}
          testId="add-vat-button"
          styling={Button.Styling.Filled}
        />
        <Spacer size={Spacer.Size.Large} />
        <Button styling={Button.Styling.Flat} disabled={loading} text="Cancel" onClick={back} />
      </FormProvider>
    </>
  )
}

export default AddVatFormContent
